// src/admin/MasterAdminApp.js
import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { 
  Users, 
  Settings, 
  Database,
  TrendingUp,
  Building2,
  Shield,
  Activity
} from 'lucide-react';

// Import Admin Components
import MasterAdminSidebar from './components/MasterAdminSidebar';
import MasterAdminHeader from './components/MasterAdminHeader';

// Import Admin Pages
import ShareTypesPage from './pages/ShareTypesPage';
import EmailTemplatesPage from './pages/EmailTemplatesPage';
import DataroomTemplatesPage from './pages/DataroomTemplatesPage';
import GeographicDataPage from './pages/GeographicDataPage';
import ImportTemplatesPage from './pages/ImportTemplatesPage';
import HealthPage from './pages/HealthPage';
import TrafficPage from './pages/TrafficPage';
import EquityAdminPage from './pages/EquityAdminPage';
import DealroomsAdminPage from './pages/DealroomsAdminPage';
import CapRaiseAdminPage from './pages/CapRaiseAdminPage';
import QuestionsAdminPage from './pages/QuestionsAdminPage';
import QuestionsSettingsPage from './pages/QuestionsSettingsPage';
import UserInvitesAdminPage from './pages/UserInvitesAdminPage';
import PlanRestrictionsPage from './pages/PlanRestrictionsPage';
import SubscriptionPlansPage from './pages/SubscriptionPlansPage';
import PromoCodesPage from './pages/PromoCodesPage';
import CurrencyManagementPage from './pages/CurrencyManagementPage';
import UsersManagementPage from './pages/UsersManagementPage';
import DatabaseToolsPage from './pages/DatabaseToolsPage';
import PaymentsPage from './pages/PaymentsPage';
import CreateInvoicePage from './pages/CreateInvoicePage';
import OneOffInvoicesPage from './pages/OneOffInvoicesPage';
import SettingsPage from './pages/SettingsPage';
import ActivityLogsPage from './pages/ActivityLogsPage';
import EmailLogsPage from './pages/EmailLogsPage';
import ApiKeysPage from './pages/ApiKeysPage';

// Import Global Store
import { useGlobalStore } from '../state/globalStore';

// Import services
import ApiService from '../services/apiService';

const MasterAdminApp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get active tab from URL path, default to 'users'
  const getActiveTabFromPath = () => {
    const path = location.pathname.replace('/adminhq', '').replace(/^\//, '');
    if (!path || path === '' || path === '/') return 'users';
    // Handle nested routes (e.g., equity/*, dataroom/*)
    const segments = path.split('/');
    return segments[0] || 'users';
  };
  
  const [activeTab, setActiveTab] = useState(() => {
    const path = location.pathname.replace('/adminhq', '').replace(/^\//, '');
    if (!path || path === '' || path === '/') return 'users';
    const segments = path.split('/');
    return segments[0] || 'users';
  });
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    users: 0,
    companies: 0,
    shareholders: 0,
    issuances: 0
  });

  // Get user and userProfile from global store
  const { user, userProfile, initUser } = useGlobalStore();

  // Initialize user on mount if not already loaded
  useEffect(() => {
    console.log('MasterAdminApp - User check:', { 
      user: user ? 'exists' : 'null', 
      userId: user?.id
    });
    if (!user) {
      console.log('MasterAdminApp - No user found, initializing...');
      initUser();
    }
  }, [user, initUser]);

  // Update activeTab when URL changes
  useEffect(() => {
    const tabFromPath = getActiveTabFromPath();
    if (tabFromPath !== activeTab) {
      setActiveTab(tabFromPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Master Admin Navigation Items
  const masterNavItems = [
    { id: 'users', name: 'Users', icon: Users, color: 'green' },
    { id: 'traffic', name: 'Traffic', icon: TrendingUp, color: 'purple' },
    { id: 'database', name: 'Database', icon: Database, color: 'red' },
    { id: 'settings', name: 'Settings', icon: Settings, color: 'gray' },
  ];

  // Product Admin Links
  const productAdminItems = [
    { 
      id: 'equity-admin', 
      name: 'Equity', 
      icon: Building2, 
      path: 'equity',
      description: 'Manage shareholders, directors, and share issuances',
      color: 'blue'
    },
    { 
      id: 'dealrooms-admin', 
      name: 'Dealrooms', 
      icon: Shield, 
      path: 'dealrooms',
      description: 'Manage dealrooms, parties, and teams',
      color: 'green'
    },
    { 
      id: 'capraise-admin', 
      name: 'CapRaise', 
      icon: TrendingUp, 
      path: 'capraise',
      description: 'Manage rounds and questions',
      color: 'purple'
    },
    { 
      id: 'questions-admin', 
      name: 'Questions', 
      icon: Activity, 
      path: 'questions',
      description: 'View all questions across all modules',
      color: 'orange'
    },
    { 
      id: 'userinvites-admin', 
      name: 'User Invites', 
      icon: Users, 
      path: 'userinvites',
      description: 'View all user invitations',
      color: 'teal'
    },
  ];

  // Color mapping for Tailwind classes (must be full class names)
  const getColorClasses = (color) => {
    const colorMap = {
      blue: {
        border: 'border-blue-500',
        text: 'text-blue-600',
        bg: 'bg-blue-100',
        textDark: 'text-blue-700',
        bgHover: 'hover:bg-blue-200'
      },
      green: {
        border: 'border-green-500',
        text: 'text-green-600',
        bg: 'bg-green-100',
        textDark: 'text-green-700',
        bgHover: 'hover:bg-green-200'
      },
      purple: {
        border: 'border-purple-500',
        text: 'text-purple-600',
        bg: 'bg-purple-100',
        textDark: 'text-purple-700',
        bgHover: 'hover:bg-purple-200'
      },
      red: {
        border: 'border-red-500',
        text: 'text-red-600',
        bg: 'bg-red-100',
        textDark: 'text-red-700',
        bgHover: 'hover:bg-red-200'
      },
      orange: {
        border: 'border-orange-500',
        text: 'text-orange-600',
        bg: 'bg-orange-100',
        textDark: 'text-orange-700',
        bgHover: 'hover:bg-orange-200'
      },
      gray: {
        border: 'border-gray-500',
        text: 'text-gray-600',
        bg: 'bg-gray-100',
        textDark: 'text-gray-700',
        bgHover: 'hover:bg-gray-200'
      }
    };
    return colorMap[color] || colorMap.blue;
  };

  // Fetch platform statistics
  const fetchStats = async () => {
    try {
      const response = await ApiService.fetchPlatformStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching platform stats:', error);
    }
  };

  useEffect(() => {
    const initializeAdmin = async () => {
      setLoading(true);
      
      // Fetch real platform stats
      await fetchStats();
      
      setLoading(false);
    };

    initializeAdmin();
  }, []);

  // eslint-disable-next-line no-unused-vars
  const renderDashboard = () => (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Master Admin Dashboard</h1>
          <p className="text-gray-600 mt-2">Manage all products and platform settings</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Welcome back</p>
          <p className="font-semibold text-gray-900">
            {(userProfile?.first_name && userProfile?.last_name) 
              ? `${userProfile.first_name} ${userProfile.last_name}` 
              : userProfile?.first_name || userProfile?.last_name || user?.email || 'Admin'}
          </p>
        </div>
      </div>

      {/* Platform Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats.users.toLocaleString()}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <Building2 className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Companies</p>
              <p className="text-2xl font-bold text-gray-900">{stats.companies.toLocaleString()}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Shareholders</p>
              <p className="text-2xl font-bold text-gray-900">{stats.shareholders.toLocaleString()}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <Activity className="h-8 w-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Share Issuances</p>
              <p className="text-2xl font-bold text-gray-900">{stats.issuances.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Product Admin Sections */}
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-900">Product Administration</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {productAdminItems.map((item) => {
            const colors = getColorClasses(item.color);
            return (
              <div
                key={item.id}
                onClick={() => navigate(item.path)}
                className={`bg-white p-6 rounded-lg shadow-sm border hover:shadow-md cursor-pointer transition-all border-l-4 ${colors.border} hover:border-l-8`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center mb-4">
                    <item.icon className={`h-10 w-10 ${colors.text}`} />
                    <div className="ml-4">
                      <h3 className="text-xl font-semibold text-gray-900">{item.name}</h3>
                      <p className="text-gray-600 text-sm mt-1">{item.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <button className={`px-4 py-2 ${colors.bg} ${colors.textDark} ${colors.bgHover} rounded-md transition-colors text-sm font-medium`}>
                      Access →
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* System Admin Sections */}
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-900">System Administration</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {masterNavItems.slice(1).map((item) => {
            const colors = getColorClasses(item.color);
            return (
              <div
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`bg-white p-4 rounded-lg shadow-sm border hover:shadow-md cursor-pointer transition-shadow border-l-4 ${colors.border}`}
              >
                <div className="flex items-center">
                  <item.icon className={`h-6 w-6 ${colors.text}`} />
                  <h3 className="text-lg font-medium text-gray-900 ml-3">{item.name}</h3>
                </div>
                <p className="text-gray-600 text-sm mt-2">
                  Manage platform-wide {item.name.toLowerCase()}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // eslint-disable-next-line no-unused-vars
  const renderSystemPage = () => {
    const getPageInfo = () => {
      switch (activeTab) {
        // Core pages
        case 'users':
          return <UsersManagementPage />;
        case 'traffic':
          return <TrafficPage />;  // Traffic and page analytics
        case 'database':
          return <DatabaseToolsPage />;
        case 'health':
          return <HealthPage />;
        case 'logs':
          return <ActivityLogsPage />;
        case 'settings':
          return <SettingsPage />;
        case 'api-keys':
          return <ApiKeysPage />;
        
        // Payments
        case 'payments':
          return <PaymentsPage />;
        case 'create-invoice':
          return <CreateInvoicePage />;
        case 'plans':
          return <SubscriptionPlansPage />;
        case 'one-off-invoices':
          return <OneOffInvoicesPage />;
        case 'plan-restrictions':
          return <PlanRestrictionsPage />;
        case 'promo-codes':
          return <PromoCodesPage />;
        case 'email-logs':
          return <EmailLogsPage />;
        
        // CMS - Hidden until fully functional
        // case 'email-templates':
        //   return <EmailTemplatesPage />;
        // case 'dataroom-templates':
        //   return <DataroomTemplatesPage />;
        // case 'import-templates':
        //   return <ImportTemplatesPage />;
        
        // Data
        case 'share-classes':
          return <ShareTypesPage />;
        case 'currencies':
          return <CurrencyManagementPage />;
        case 'email-templates':
          return <EmailTemplatesPage />;
        case 'dataroom-templates':
          return <DataroomTemplatesPage />;
        case 'geo-data':
          return <GeographicDataPage />;
        case 'import-templates':
          return <ImportTemplatesPage />;
        
        default:
          // Instead of showing ComingSoonPage, show a simple message
          return (
            <div className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Page Not Found</h2>
              <p className="text-gray-600">The requested page is not available or is still under development.</p>
              <button
                onClick={() => setActiveTab('dashboard')}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Back to Dashboard
              </button>
            </div>
          );
      }
    };

    return getPageInfo();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  // Helper to navigate and update state
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    navigate(`/adminhq/${tabId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Routes>
        {/* Redirect root to users */}
        <Route index element={<Navigate to="/adminhq/users" replace />} />
        
        {/* Master Admin Pages with distinct URLs */}
        <Route path="users" element={
          <div className="flex w-full">
            <MasterAdminSidebar activeTab={activeTab} setActiveTab={handleTabChange} />
            <div className="flex-1 flex flex-col">
              <MasterAdminHeader user={user} userProfile={userProfile} />
              <main className="flex-1 overflow-auto">
                <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <UsersManagementPage />
                </div>
              </main>
            </div>
          </div>
        } />
        
        <Route path="traffic" element={
          <div className="flex w-full">
            <MasterAdminSidebar activeTab={activeTab} setActiveTab={handleTabChange} />
            <div className="flex-1 flex flex-col">
              <MasterAdminHeader user={user} userProfile={userProfile} />
              <main className="flex-1 overflow-auto">
                <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <TrafficPage />
                </div>
              </main>
            </div>
          </div>
        } />
        
        <Route path="database" element={
          <div className="flex w-full">
            <MasterAdminSidebar activeTab={activeTab} setActiveTab={handleTabChange} />
            <div className="flex-1 flex flex-col">
              <MasterAdminHeader user={user} userProfile={userProfile} />
              <main className="flex-1 overflow-auto">
                <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <DatabaseToolsPage />
                </div>
              </main>
            </div>
          </div>
        } />
        
        <Route path="health" element={
          <div className="flex w-full">
            <MasterAdminSidebar activeTab={activeTab} setActiveTab={handleTabChange} />
            <div className="flex-1 flex flex-col">
              <MasterAdminHeader user={user} userProfile={userProfile} />
              <main className="flex-1 overflow-auto">
                <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <HealthPage />
                </div>
              </main>
            </div>
          </div>
        } />
        
        <Route path="logs" element={
          <div className="flex w-full">
            <MasterAdminSidebar activeTab={activeTab} setActiveTab={handleTabChange} />
            <div className="flex-1 flex flex-col">
              <MasterAdminHeader user={user} userProfile={userProfile} />
              <main className="flex-1 overflow-auto">
                <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <ActivityLogsPage />
                </div>
              </main>
            </div>
          </div>
        } />
        
        <Route path="settings" element={
          <div className="flex w-full">
            <MasterAdminSidebar activeTab={activeTab} setActiveTab={handleTabChange} />
            <div className="flex-1 flex flex-col">
              <MasterAdminHeader user={user} userProfile={userProfile} />
              <main className="flex-1 overflow-auto">
                <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <SettingsPage />
                </div>
              </main>
            </div>
          </div>
        } />
        
        <Route path="api-keys" element={
          <div className="flex w-full">
            <MasterAdminSidebar activeTab={activeTab} setActiveTab={handleTabChange} />
            <div className="flex-1 flex flex-col">
              <MasterAdminHeader user={user} userProfile={userProfile} />
              <main className="flex-1 overflow-auto">
                <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <ApiKeysPage />
                </div>
              </main>
            </div>
          </div>
        } />
        
        <Route path="payments" element={
          <div className="flex w-full">
            <MasterAdminSidebar activeTab={activeTab} setActiveTab={handleTabChange} />
            <div className="flex-1 flex flex-col">
              <MasterAdminHeader user={user} userProfile={userProfile} />
              <main className="flex-1 overflow-auto">
                <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <PaymentsPage />
                </div>
              </main>
            </div>
          </div>
        } />
        
        <Route path="create-invoice" element={
          <div className="flex w-full">
            <MasterAdminSidebar activeTab={activeTab} setActiveTab={handleTabChange} />
            <div className="flex-1 flex flex-col">
              <MasterAdminHeader user={user} userProfile={userProfile} />
              <main className="flex-1 overflow-auto">
                <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <CreateInvoicePage />
                </div>
              </main>
            </div>
          </div>
        } />
        
        <Route path="plans" element={
          <div className="flex w-full">
            <MasterAdminSidebar activeTab={activeTab} setActiveTab={handleTabChange} />
            <div className="flex-1 flex flex-col">
              <MasterAdminHeader user={user} userProfile={userProfile} />
              <main className="flex-1 overflow-auto">
                <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <SubscriptionPlansPage />
                </div>
              </main>
            </div>
          </div>
        } />
        
        <Route path="one-off-invoices" element={
          <div className="flex w-full">
            <MasterAdminSidebar activeTab={activeTab} setActiveTab={handleTabChange} />
            <div className="flex-1 flex flex-col">
              <MasterAdminHeader user={user} userProfile={userProfile} />
              <main className="flex-1 overflow-auto">
                <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <OneOffInvoicesPage />
                </div>
              </main>
            </div>
          </div>
        } />
        
        <Route path="plan-restrictions" element={
          <div className="flex w-full">
            <MasterAdminSidebar activeTab={activeTab} setActiveTab={handleTabChange} />
            <div className="flex-1 flex flex-col">
              <MasterAdminHeader user={user} userProfile={userProfile} />
              <main className="flex-1 overflow-auto">
                <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <PlanRestrictionsPage />
                </div>
              </main>
            </div>
          </div>
        } />
        
        <Route path="promo-codes" element={
          <div className="flex w-full">
            <MasterAdminSidebar activeTab={activeTab} setActiveTab={handleTabChange} />
            <div className="flex-1 flex flex-col">
              <MasterAdminHeader user={user} userProfile={userProfile} />
              <main className="flex-1 overflow-auto">
                <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <PromoCodesPage />
                </div>
              </main>
            </div>
          </div>
        } />
        
        <Route path="share-classes" element={
          <div className="flex w-full">
            <MasterAdminSidebar activeTab={activeTab} setActiveTab={handleTabChange} />
            <div className="flex-1 flex flex-col">
              <MasterAdminHeader user={user} userProfile={userProfile} />
              <main className="flex-1 overflow-auto">
                <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <ShareTypesPage />
                </div>
              </main>
            </div>
          </div>
        } />
        
        <Route path="currencies" element={
          <div className="flex w-full">
            <MasterAdminSidebar activeTab={activeTab} setActiveTab={handleTabChange} />
            <div className="flex-1 flex flex-col">
              <MasterAdminHeader user={user} userProfile={userProfile} />
              <main className="flex-1 overflow-auto">
                <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <CurrencyManagementPage />
                </div>
              </main>
            </div>
          </div>
        } />
        
        <Route path="email-logs" element={
          <div className="flex w-full">
            <MasterAdminSidebar activeTab={activeTab} setActiveTab={handleTabChange} />
            <div className="flex-1 flex flex-col">
              <MasterAdminHeader user={user} userProfile={userProfile} />
              <main className="flex-1 overflow-auto">
                <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <EmailLogsPage />
                </div>
              </main>
            </div>
          </div>
        } />
        
        <Route path="email-templates" element={
          <div className="flex w-full">
            <MasterAdminSidebar activeTab={activeTab} setActiveTab={handleTabChange} />
            <div className="flex-1 flex flex-col">
              <MasterAdminHeader user={user} userProfile={userProfile} />
              <main className="flex-1 overflow-auto">
                <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <EmailTemplatesPage />
                </div>
              </main>
            </div>
          </div>
        } />
        
        <Route path="dataroom-templates" element={
          <div className="flex w-full">
            <MasterAdminSidebar activeTab={activeTab} setActiveTab={handleTabChange} />
            <div className="flex-1 flex flex-col">
              <MasterAdminHeader user={user} userProfile={userProfile} />
              <main className="flex-1 overflow-auto">
                <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <DataroomTemplatesPage />
                </div>
              </main>
            </div>
          </div>
        } />
        
        <Route path="geo-data" element={
          <div className="flex w-full">
            <MasterAdminSidebar activeTab={activeTab} setActiveTab={handleTabChange} />
            <div className="flex-1 flex flex-col">
              <MasterAdminHeader user={user} userProfile={userProfile} />
              <main className="flex-1 overflow-auto">
                <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <GeographicDataPage />
                </div>
              </main>
            </div>
          </div>
        } />
        
        <Route path="import-templates" element={
          <div className="flex w-full">
            <MasterAdminSidebar activeTab={activeTab} setActiveTab={handleTabChange} />
            <div className="flex-1 flex flex-col">
              <MasterAdminHeader user={user} userProfile={userProfile} />
              <main className="flex-1 overflow-auto">
                <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <ImportTemplatesPage />
                </div>
              </main>
            </div>
          </div>
        } />
        
        {/* Product Admin Routes with nested tabs */}
        <Route path="equity" element={<Navigate to="/adminhq/equity/companies" replace />} />
        <Route path="equity/*" element={<EquityAdminPage />} />
        <Route path="dealrooms" element={<Navigate to="/adminhq/dealrooms/dealrooms" replace />} />
        <Route path="dealrooms/*" element={<DealroomsAdminPage />} />
        <Route path="capraise" element={<Navigate to="/adminhq/capraise/rounds" replace />} />
        <Route path="capraise/*" element={<CapRaiseAdminPage />} />

        {/* Other Product Routes */}
        <Route path="questions/*" element={<QuestionsAdminPage />} />
        <Route path="questions-settings" element={
          <div className="flex w-full">
            <MasterAdminSidebar activeTab={activeTab} setActiveTab={handleTabChange} />
            <div className="flex-1 flex flex-col">
              <MasterAdminHeader user={user} userProfile={userProfile} />
              <main className="flex-1 overflow-auto">
                <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <QuestionsSettingsPage />
                </div>
              </main>
            </div>
          </div>
        } />
        <Route path="userinvites" element={
          <div className="flex w-full">
            <MasterAdminSidebar activeTab={activeTab} setActiveTab={handleTabChange} />
            <div className="flex-1 flex flex-col">
              <MasterAdminHeader user={user} userProfile={userProfile} />
              <main className="flex-1 overflow-auto">
                <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <UserInvitesAdminPage />
                </div>
              </main>
            </div>
          </div>
        } />
      </Routes>
    </div>
  );
};

export default MasterAdminApp;